/**
 * Outlook-style time entry: a text input paired with a dropdown of 15-minute
 * increments, displayed 12-hour, stored 24-hour ("HH:MM" — the API contract).
 * Free-form typing is allowed and parsed leniently ("2pm", "2:30 PM", "14:30").
 * When `baseTime` is set (the event's start), the list begins just after it
 * and each option is labeled with its duration, mirroring Outlook's end-time
 * picker.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface TimeComboBoxProps {
  id?: string;
  /** "HH:MM" 24h, or '' for unset */
  value: string;
  onChange: (value: string) => void;
  /** Start time ("HH:MM") — switches the list to duration-labeled end options */
  baseTime?: string;
  placeholder?: string;
  className?: string;
}

const QUARTER_HOURS: string[] = [];
for (let h = 0; h < 24; h += 1) {
  for (let m = 0; m < 60; m += 15) {
    QUARTER_HOURS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

export function to12h(value: string): string {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return '';
  let hour = parseInt(match[1], 10);
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${match[2]} ${meridiem}`;
}

/** Lenient parse: "2pm", "2 PM", "2:30pm", "14:30", "9". Returns "HH:MM" or null. */
export function parseTime(text: string): string | null {
  const s = text.trim().toLowerCase();
  if (!s) return null;
  const match = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(a|p)?\.?m?\.?$/);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = match[2] ? parseInt(match[2], 10) : 0;
  if (minute > 59) return null;
  const meridiem = match[3];
  if (meridiem === 'p' && hour < 12) hour += 12;
  if (meridiem === 'a' && hour === 12) hour = 0;
  if (!meridiem && hour > 23) return null;
  if (hour > 23) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function addMinutes(value: string, minutes: number): string {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return value;
  const total = (parseInt(match[1], 10) * 60 + parseInt(match[2], 10) + minutes + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function minutesBetween(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

function durationLabel(minutes: number): string {
  if (minutes <= 0) return '';
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours} hr${hours > 1 ? 's' : ''}` : `${hours.toFixed(1)} hrs`;
}

const TimeComboBox: React.FC<TimeComboBoxProps> = ({
  id,
  value,
  onChange,
  baseTime,
  placeholder = '--:--',
  className = '',
}) => {
  const [text, setText] = useState(to12h(value));
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setText(to12h(value));
  }, [value]);

  // End-time mode: options start 15 minutes after baseTime, wrap the day, and
  // carry duration labels — the Outlook pattern
  const options = useMemo(() => {
    if (!baseTime || !/^\d{2}:\d{2}$/.test(baseTime)) {
      return QUARTER_HOURS.map((time) => ({ time, label: to12h(time) }));
    }
    const startIdx = QUARTER_HOURS.findIndex((t) => minutesBetween(baseTime, t) > 0);
    const rotated = startIdx === -1
      ? QUARTER_HOURS
      : [...QUARTER_HOURS.slice(startIdx), ...QUARTER_HOURS.slice(0, startIdx)];
    return rotated.map((time) => {
      const duration = minutesBetween(baseTime, time);
      return {
        time,
        label: duration > 0 ? `${to12h(time)} (${durationLabel(duration)})` : to12h(time),
      };
    });
  }, [baseTime]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll the selected (or first) option into view when the list opens
  useEffect(() => {
    if (!open || !listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    (selected ?? listRef.current.firstElementChild)?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  const commitText = () => {
    if (!text.trim()) {
      onChange('');
      return;
    }
    const parsed = parseTime(text);
    if (parsed) {
      onChange(parsed);
      setText(to12h(parsed));
    } else {
      setText(to12h(value)); // revert unparseable input
    }
  };

  const pick = (time: string) => {
    onChange(time);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={text}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={commitText}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commitText(); setOpen(false); }
          if (e.key === 'Escape') setOpen(false);
        }}
        className="w-full rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-tertiary px-3 py-1.5 text-sm text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-primary-400"
      />
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-30 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-secondary shadow-lg text-sm"
        >
          {options.map(({ time, label }) => (
            <li key={time} role="option" aria-selected={time === value} data-selected={time === value}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault() /* keep input focus; avoid blur-commit race */}
                onClick={() => pick(time)}
                className={`w-full px-3 py-1 text-left hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary ${
                  time === value ? 'bg-primary-tint dark:bg-primary-900/20 font-semibold' : ''
                }`}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TimeComboBox;
